/**
 * Poli Poker scoring (authoritative — from "Political Poker Scoring" doc).
 *
 * Each hand, every player scores:  position points + hand value + contig bonus.
 *   Position:  1st=5, 2nd=3, 3rd=2, 4th=1, 5th=0
 *   Hand value: equals the HandCategory index (Zipper 0 .. Royal Flush 9)
 *   Contig:    +1 if the state you WIN this hand borders a state you already own
 *              (only the hand winner can earn it).
 *
 * Worked examples from the doc (see selftest.ts):
 *   - Win Washington w/ full house, already own Hawaii → 5 + 6 + 1 = 12
 *   - Win Maryland w/ a straight (no neighbour owned) → 5 + 4 = 9
 *   - Come 3rd in Tennessee w/ three of a kind → 2 + 3 = 5
 */
import { borders } from "./states.ts";
import type { HandCategory } from "./handEval.ts";

/** Position 1..5 → points. Positions beyond 5 score 0. */
export const POSITION_POINTS: readonly number[] = [0, 5, 3, 2, 1, 0];

export function positionPoints(position: number): number {
  return POSITION_POINTS[position] ?? 0;
}

/** Hand value points == category index (0..9). */
export function handValuePoints(category: HandCategory): number {
  return category;
}

/**
 * +1 if `wonState` borders any state the winner already owns.
 * `ownedBefore` = states the winner held BEFORE claiming this one.
 */
export function contiguousBonus(wonState: string, ownedBefore: readonly string[]): number {
  return ownedBefore.some((s) => borders(wonState, s)) ? 1 : 0;
}

export interface HandScoreInput {
  position: number; // 1..5
  category: HandCategory;
  isWinner: boolean;
  wonState?: string; // required when isWinner
  ownedBefore?: readonly string[]; // winner's states before this hand
}

export interface HandScoreBreakdown {
  position: number;
  handValue: number;
  contig: number;
  total: number;
}

export function scoreHand(input: HandScoreInput): HandScoreBreakdown {
  const position = positionPoints(input.position);
  const handValue = handValuePoints(input.category);
  const contig =
    input.isWinner && input.wonState
      ? contiguousBonus(input.wonState, input.ownedBefore ?? [])
      : 0;
  return { position, handValue, contig, total: position + handValue + contig };
}
